




























import { Vue, Component, Prop } from 'vue-property-decorator';
import { Node as ProsemirrorNode } from 'prosemirror-model';
import { EditorView } from 'prosemirror-view';
import { Transaction } from 'prosemirror-state';
import moment from 'moment';
import { IField, IMergeValues } from '@/editor/types';
import { EFieldType, EMergeCategory, EMergeKey } from '@/editor/types/enums';
import { IRecipient } from '@lib-shared/types/Recipient';
import { ProsemirrorHelper } from '@/editor/helpers/prosemirror.helper';
import { toolTipOptions } from '@/editor/editor-block/constants';
import { IUser } from '@/types/User';
import { IDocumentValues } from '@/editor/types/merge-values';
import { IEntity } from '@/entity/types/entity';

@Component({
  data: () => ({
    toolTipOptions,
  }),
})
export default class MergeTagView extends Vue {
  @Prop() node: ProsemirrorNode;
  @Prop() options: {
    onOpenMenu: () => {};
    mergeValues: IMergeValues;
    fields: IField[];
    isTemplate: boolean;
  };
  @Prop() selected: boolean;
  @Prop() view: EditorView;
  @Prop() getPos: () => number;

  mergeTagPlaceholder = '{{';

  get mergeKeyAttribute(): string {
    return this.node.attrs.mergeKey;
  }

  get categoryAttribute(): string {
    return this.node.attrs.category;
  }

  get tagId(): string {
    return this.node.attrs.tagId;
  }

  get customName(): string {
    return this.node.attrs.customName;
  }

  get field(): IField {
    return this.options?.fields?.find(({ id }: IField) => id === this.tagId);
  }

  get documentValues(): IDocumentValues {
    return this.options?.mergeValues?.document;
  }

  get sender(): IUser {
    return this.options?.mergeValues?.sender;
  }

  get entity(): IEntity {
    return this.options?.mergeValues?.entity as IEntity;
  }

  get recipient(): IRecipient {
    return this.options?.mergeValues?.recipients?.find(
      ({ id }: IRecipient) => this.recipientId === id
    );
  }

  get category(): EMergeCategory {
    return this.field?.category;
  }

  get fieldType(): EFieldType {
    return this.field?.type;
  }

  get mergeKey(): EMergeKey {
    return this.field?.mergeKey;
  }

  get recipientId(): string {
    return this.field?.recipientId;
  }

  get mergeLabel(): string {
    return String(this.customName || this.mergeKey).toLowerCase();
  }

  get mergeTagType(): string {
    return String(
      this.fieldType === EFieldType.Custom ? EFieldType.Custom : this.category
    ).toLowerCase();
  }

  mergeValue(): string {
    if (this.fieldType === EFieldType.Custom) {
      return this.field.value || this.field.customName;
    }

    switch (this.category || this.fieldType) {
      case EMergeCategory.Recipient:
        return this.recipientValue;

      case EMergeCategory.Entity:
        return this.entity?.[this.mergeKey] || `${this.category}.${this.mergeKey}`;

      case EMergeCategory.Sender:
        return this.sender?.[this.mergeKey] || `${this.category}.${this.mergeKey}`;

      case EMergeCategory.Document:
        return this.documentValue || `${this.category}.${this.mergeKey}`;

      default:
        return this.mergeTagPlaceholder;
    }
  }

  get documentValue(): string {
    const value: string = this.documentValues?.[this.mergeKey];

    if (!value) {
      return '';
    }

    if (this.mergeKey === EMergeKey.sendDate || this.mergeKey === EMergeKey.expirationDate) {
      return moment(value).format('LLL');
    }

    return value;
  }

  get recipientValue(): string {
    return (
      this.recipientNotFoundOrRole ||
      this.recipient?.[this.mergeKey] ||
      `${this.recipient.firstName + this.recipient.lastName}.${this.mergeKey}`
    );
  }

  get recipientNotFoundOrRole(): string | false {
    if (!this.recipient) {
      return this.$t('missing-recipient').toString();
    }

    if (!!this.recipient && !this.recipient.email) {
      return `${this.recipient.roleName}.${this.mergeKey}`;
    }

    return false;
  }

  get recipientTooltip(): string {
    return (
      this.recipientNotFoundOrRole ||
      `${this.recipient.firstName + this.recipient.lastName}.${this.mergeKey}`
    );
  }

  get tooltipContent(): string {
    if (this.fieldType === EFieldType.Custom && this.field.value) {
      return this.field.customName;
    }

    if (!this.category && this.fieldType !== EFieldType.Custom) {
      return this.mergeKey;
    }

    if (this.category === EMergeCategory.Recipient && !this.recipient) {
      return this.$t('you-need-to-connect-a-new-recipient').toString();
    }

    if (this.isMergeValueEmpty) {
      return `${this.$t('value-missing').toString()} ${this.mergeValue()}`;
    }

    if (this.category === EMergeCategory.Recipient) {
      return this.recipientTooltip;
    }

    return `${this.category}.${this.mergeKey}`;
  }

  get isRoleInTemplate(): boolean {
    return (
      this.options?.isTemplate &&
      this.category === EMergeCategory.Recipient &&
      !!this.recipient &&
      !this.recipient.email
    );
  }

  get isMergeValueEmpty(): boolean {
    if (this.fieldType === EFieldType.Custom) {
      return !this.field.value;
    }

    if (this.mergeKey === EMergeKey.sendDate || this.isRoleInTemplate) {
      return false;
    }

    if (this.category === EMergeCategory.Recipient) {
      return !this.recipient || !this.recipient?.email || !this.recipient?.[this.mergeKey];
    }

    return this.mergeValue() === `${this.category}.${this.mergeKey}`;
  }

  mounted() {
    if (this.field) {
      return;
    }
    this.$nextTick(() => {
      this.openMenu();
    });
  }

  selectMergeTag() {
    const selection: Transaction = ProsemirrorHelper.selectNode(this.view.state.tr, this.getPos());
    this.view.dispatch(selection);
  }

  openMenu() {
    this.options.onOpenMenu();
  }
}
